import { useCallback, useEffect, useMemo } from 'react';
import { useAuthSessionStore } from '../../auth/session-store';
import { usePromptStore } from '../store/prompts';
import { useCloudPromptsStore } from '../store/cloud-prompts';
import type { Folder, Prompt } from '../types/prompt';
import type { CloudFolder, CloudPrompt } from '../types/cloud';

export interface UnifiedPrompt {
  id: string;
  title: string;
  content: string;
  folderId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface UnifiedFolder {
  id: string;
  name: string;
  createdAt: number;
}

export interface UsePromptSourceResult {
  isCloudSource: boolean;
  isLoading: boolean;
  error: string | null;
  prompts: UnifiedPrompt[];
  folders: UnifiedFolder[];
  searchFilter: string;
  loadPrompts: () => Promise<void>;
  loadFolders: () => Promise<void>;
  addPrompt: (title: string, content: string, folderId?: string | null) => Promise<void>;
  updatePrompt: (id: string, title: string, content: string, folderId?: string | null) => Promise<void>;
  deletePrompt: (id: string) => Promise<void>;
  setSearchFilter: (filter: string) => void;
  addFolder: (name: string) => Promise<string | null>;
  updateFolder: (id: string, name: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  clearError: () => void;
}

function cloudToUnifiedPrompt(prompt: CloudPrompt): UnifiedPrompt {
  return {
    id: prompt.id,
    title: prompt.title,
    content: prompt.content,
    folderId: prompt.folderId,
    createdAt: new Date(prompt.createdAt).getTime(),
    updatedAt: new Date(prompt.updatedAt).getTime(),
  };
}

function cloudToUnifiedFolder(folder: CloudFolder): UnifiedFolder {
  return {
    id: folder.id,
    name: folder.name,
    createdAt: new Date(folder.createdAt).getTime(),
  };
}

function localToUnifiedPrompt(prompt: Prompt): UnifiedPrompt {
  return {
    id: prompt.id,
    title: prompt.title,
    content: prompt.content,
    folderId: prompt.folderId,
    createdAt: prompt.createdAt,
    updatedAt: prompt.updatedAt,
  };
}

function localToUnifiedFolder(folder: Folder): UnifiedFolder {
  return {
    id: folder.id,
    name: folder.name,
    createdAt: folder.createdAt,
  };
}

export function usePromptSource(): UsePromptSourceResult {
  const isAuthenticated = useAuthSessionStore((state) => state.isAuthenticated);
  const isPro = useAuthSessionStore((state) => state.subscription?.isPro ?? false);
  const isCloudSource = isPro && isAuthenticated;

  const localPrompts = usePromptStore((state) => state.prompts);
  const localFolders = usePromptStore((state) => state.folders);
  const localIsLoading = usePromptStore((state) => state.isLoading);
  const localError = usePromptStore((state) => state.error);
  const localSearchFilter = usePromptStore((state) => state.searchFilter);
  const localLoadPrompts = usePromptStore((state) => state.loadPrompts);
  const localLoadFolders = usePromptStore((state) => state.loadFolders);
  const localAddPrompt = usePromptStore((state) => state.addPrompt);
  const localUpdatePrompt = usePromptStore((state) => state.updatePrompt);
  const localDeletePrompt = usePromptStore((state) => state.deletePrompt);
  const localSetSearchFilter = usePromptStore((state) => state.setSearchFilter);
  const localAddFolder = usePromptStore((state) => state.addFolder);
  const localUpdateFolder = usePromptStore((state) => state.updateFolder);
  const localDeleteFolder = usePromptStore((state) => state.deleteFolder);

  const cloudPrompts = useCloudPromptsStore((state) => state.prompts);
  const cloudFolders = useCloudPromptsStore((state) => state.folders);
  const cloudIsLoading = useCloudPromptsStore((state) => state.isLoading);
  const cloudError = useCloudPromptsStore((state) => state.error);
  const cloudSearchFilter = useCloudPromptsStore((state) => state.searchFilter);
  const cloudFetchPrompts = useCloudPromptsStore((state) => state.fetchPrompts);
  const cloudFetchFolders = useCloudPromptsStore((state) => state.fetchFolders);
  const cloudCreatePrompt = useCloudPromptsStore((state) => state.createPrompt);
  const cloudUpdatePrompt = useCloudPromptsStore((state) => state.updatePrompt);
  const cloudDeletePrompt = useCloudPromptsStore((state) => state.deletePrompt);
  const cloudSetSearchFilter = useCloudPromptsStore((state) => state.setSearchFilter);
  const cloudCreateFolder = useCloudPromptsStore((state) => state.createFolder);
  const cloudRenameFolder = useCloudPromptsStore((state) => state.renameFolder);
  const cloudDeleteFolder = useCloudPromptsStore((state) => state.deleteFolder);
  const cloudClearError = useCloudPromptsStore((state) => state.clearError);

  const prompts = useMemo(() => {
    if (isCloudSource) {
      return cloudPrompts.map(cloudToUnifiedPrompt);
    }

    return localPrompts.map(localToUnifiedPrompt);
  }, [isCloudSource, cloudPrompts, localPrompts]);

  const folders = useMemo(() => {
    if (isCloudSource) {
      return cloudFolders.map(cloudToUnifiedFolder);
    }

    return localFolders.map(localToUnifiedFolder);
  }, [isCloudSource, cloudFolders, localFolders]);

  const isLoading = isCloudSource ? cloudIsLoading : localIsLoading;
  const error = isCloudSource ? cloudError : localError;
  const searchFilter = isCloudSource ? cloudSearchFilter : localSearchFilter;

  const loadPrompts = useCallback(async () => {
    if (isCloudSource) {
      await cloudFetchPrompts();
      return;
    }

    await localLoadPrompts();
  }, [isCloudSource, cloudFetchPrompts, localLoadPrompts]);

  const loadFolders = useCallback(async () => {
    if (isCloudSource) {
      await cloudFetchFolders();
      return;
    }

    await localLoadFolders();
  }, [isCloudSource, cloudFetchFolders, localLoadFolders]);

  const addPrompt = useCallback(async (title: string, content: string, folderId: string | null = null) => {
    if (isCloudSource) {
      await cloudCreatePrompt({ title, content, folderId });
      return;
    }

    await localAddPrompt(title, content, folderId);
  }, [isCloudSource, cloudCreatePrompt, localAddPrompt]);

  const updatePrompt = useCallback(async (id: string, title: string, content: string, folderId?: string | null) => {
    if (isCloudSource) {
      await cloudUpdatePrompt(id, { title, content, folderId });
      return;
    }

    await localUpdatePrompt(id, title, content, folderId);
  }, [isCloudSource, cloudUpdatePrompt, localUpdatePrompt]);

  const deletePrompt = useCallback(async (id: string) => {
    if (isCloudSource) {
      await cloudDeletePrompt(id);
      return;
    }

    await localDeletePrompt(id);
  }, [isCloudSource, cloudDeletePrompt, localDeletePrompt]);

  const setSearchFilter = useCallback((filter: string) => {
    if (isCloudSource) {
      cloudSetSearchFilter(filter);
      return;
    }

    localSetSearchFilter(filter);
  }, [isCloudSource, cloudSetSearchFilter, localSetSearchFilter]);

  const addFolder = useCallback(async (name: string): Promise<string | null> => {
    if (isCloudSource) {
      const result = await cloudCreateFolder({ name });
      return result?.id ?? null;
    }

    return localAddFolder(name);
  }, [isCloudSource, cloudCreateFolder, localAddFolder]);

  const updateFolder = useCallback(async (id: string, name: string) => {
    if (isCloudSource) {
      await cloudRenameFolder(id, { name });
      return;
    }

    await localUpdateFolder(id, name);
  }, [isCloudSource, cloudRenameFolder, localUpdateFolder]);

  const deleteFolder = useCallback(async (id: string) => {
    if (isCloudSource) {
      await cloudDeleteFolder(id);
      return;
    }

    await localDeleteFolder(id);
  }, [isCloudSource, cloudDeleteFolder, localDeleteFolder]);

  const clearError = useCallback(() => {
    if (isCloudSource) {
      cloudClearError();
    }
  }, [isCloudSource, cloudClearError]);

  useEffect(() => {
    void loadPrompts();
    void loadFolders();
  }, [isCloudSource, loadPrompts, loadFolders]);

  return {
    isCloudSource,
    isLoading,
    error,
    prompts,
    folders,
    searchFilter,
    loadPrompts,
    loadFolders,
    addPrompt,
    updatePrompt,
    deletePrompt,
    setSearchFilter,
    addFolder,
    updateFolder,
    deleteFolder,
    clearError,
  };
}
